import { useState, useEffect, useMemo } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ThemeToggle } from "../components";
import NotificationBell from "../components/NotificationBell";
import CriticalAlertBanner from "../components/CriticalAlertBanner";
import { useTheme } from "../hooks/useTheme";
import { useSSE } from "../hooks/useSSE";
import { apiGet } from "../lib/api";

interface NavItem {
  label: string;
  path: string;
  icon: string;
  section: string;
}

interface AlertSummary {
  critical: number;
  warning: number;
  open: number;
}

interface FleetPulse {
  activeDeployments: number;
  degradedDeployments: number;
  offlineDeployments: number;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Fleet Overview", path: "/fleet", icon: "grid", section: "Fleet" },
  { label: "Queue", path: "/queue", icon: "pulse", section: "Fleet" },
  { label: "Tenants", path: "/tenants", icon: "users", section: "Tenants" },
  { label: "Versions", path: "/versions", icon: "tag", section: "Fleet" },
  // BUCKET I.8 — Releases cockpit is now the fleet-upgrade orchestrator
  // and must be reachable from the main nav. Previously hidden as a
  // stocktake surface.
  { label: "Releases", path: "/releases", icon: "rocket", section: "Fleet" },
  { label: "Learning", path: "/learning", icon: "brain", section: "Intelligence" },
  { label: "Inference", path: "/inference", icon: "zap", section: "Intelligence" },
  { label: "Action Center", path: "/actions", icon: "shield", section: "Monitoring" },
  { label: "Labs", path: "/labs", icon: "beaker", section: "Monitoring" },
  { label: "Usage Analytics", path: "/usage", icon: "chart", section: "Monitoring" },
  { label: "Audit Log", path: "/audit", icon: "scroll", section: "Monitoring" },
  { label: "Backups", path: "/backups", icon: "archive", section: "Monitoring" },
  { label: "Readiness", path: "/readiness", icon: "check", section: "Monitoring" },
  { label: "Waitlist", path: "/waitlist/manage", icon: "clipboard", section: "Monitoring" },
  { label: "Email", path: "/emails", icon: "mail", section: "Communications" },
];

const ICONS: Record<string, string> = {
  grid: "M4 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5Zm10 0a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V5ZM4 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-4Zm10 0a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4Z",
  rocket:
    "M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.63 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-3.32 3.32c.177-.592.486-1.16.9-1.66a4.47 4.47 0 0 1 2.42-1.66Z",
  server:
    "M2 5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5Zm2 8a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H4Zm1-6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
  users:
    "M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z",
  brain:
    "M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5l-2.47 2.47a.75.75 0 0 1-.53.22H8a.75.75 0 0 1-.53-.22L5 14.5m14 0V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v9.5",
  zap: "m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z",
  scroll:
    "M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z",
  tag: "M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z M6 6h.008v.008H6V6Z",
  archive:
    "M20.25 7.5l-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z",
  chart:
    "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z",
  package:
    "M20.25 7.5l-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z",
  clipboard:
    "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9 2 2 4-4",
  mail: "M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75",
  check: "M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  shield:
    "M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z",
  pulse: "M3 12h3l3-9 4 18 3-9h5",
  beaker:
    "M9 3h6M10 3v5.586a1 1 0 0 1-.293.707l-4.95 4.95A2 2 0 0 0 4.172 16H19.83a2 2 0 0 0-1.414-1.757l-4.95-4.95A1 1 0 0 1 13 8.586V3m-4 13h6",
  collapse: "M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5",
};

const MOBILE_BREAKPOINT = 768;

function NavIcon({ name, size = 20 }: { name: string; size?: number }) {
  const path = ICONS[name];
  if (!path) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={path} />
    </svg>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isMobile;
}

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const isMobile = useIsMobile();

  // Real-time event stream for alert updates
  useSSE();

  const { data: alertSummary } = useQuery<AlertSummary>({
    queryKey: ["alerts-summary"],
    queryFn: () => apiGet("/api/alerts/summary"),
    refetchInterval: 30_000,
  });

  const { data: fleetPulse } = useQuery<FleetPulse>({
    queryKey: ["fleet", "overview"],
    queryFn: () => apiGet("/api/fleet/overview"),
    refetchInterval: 30_000,
  });

  // Close mobile drawer on navigation
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  // Group nav items by section
  const sections = NAV_ITEMS.reduce<Record<string, NavItem[]>>((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const activeNavItem = useMemo(
    () => NAV_ITEMS.find((item) => isActive(item.path)) ?? NAV_ITEMS[0],
    [location.pathname],
  );
  const openAlerts = alertSummary?.open ?? 0;
  const serviceState =
    (fleetPulse?.offlineDeployments ?? 0) > 0
      ? "degraded"
      : (fleetPulse?.degradedDeployments ?? 0) > 0
        ? "watching"
        : "healthy";
  const serviceColor =
    serviceState === "healthy"
      ? "var(--c-success)"
      : serviceState === "watching"
        ? "var(--c-warning)"
        : "var(--c-danger)";

  // On mobile: sidebar is hidden by default, shown as overlay when mobileOpen
  // On desktop: sidebar uses collapsed state as before
  const showSidebar = isMobile ? mobileOpen : true;
  const sidebarWidth = isMobile ? 240 : collapsed ? 60 : 240;
  const sidebarCollapsed = isMobile ? false : collapsed;

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile backdrop overlay */}
      {isMobile && mobileOpen && (
        <div
          data-testid="mobile-backdrop"
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 39,
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          backgroundColor: "var(--c-surface)",
          borderRight: "1px solid var(--c-border, rgba(0,0,0,0.1))",
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.2s ease, width 0.2s ease, min-width 0.2s ease",
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 40,
          transform: showSidebar ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        {/* Logo / title area */}
        <div
          style={{
            padding: sidebarCollapsed ? "16px 12px" : "16px 20px",
            borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))",
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            gap: 8,
          }}
        >
          {!sidebarCollapsed && (
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: 20,
                fontWeight: 400,
                color: "var(--c-text)",
                whiteSpace: "nowrap",
              }}
            >
              Agent Me
            </span>
          )}
          {isMobile ? (
            <button
              onClick={() => setMobileOpen(false)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--c-text-secondary)",
                padding: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Close sidebar"
              aria-label="Close sidebar"
            >
              <svg
                width={18}
                height={18}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => setCollapsed(!collapsed)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--c-text-secondary)",
                padding: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <NavIcon name="collapse" size={18} />
            </button>
          )}
        </div>

        {!sidebarCollapsed && (
          <div
            style={{
              padding: "10px 12px",
              borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.08))",
            }}
          >
            <div
              style={{
                borderRadius: 10,
                padding: "10px 12px",
                border: "1px solid var(--c-border)",
                backgroundColor: "var(--c-surface)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--c-text-faint)",
                  }}
                >
                  Platform Pulse
                </span>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: serviceColor,
                  }}
                />
              </div>
              <div style={{ fontSize: 12, color: "var(--c-text-secondary)" }}>
                {(fleetPulse?.activeDeployments ?? 0).toLocaleString()} live
                {" · "}
                {openAlerts} alerts
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto" }}>
          {Object.entries(sections).map(([section, items]) => (
            <div key={section} style={{ marginBottom: 16 }}>
              {!sidebarCollapsed && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "var(--c-text-faint)",
                    padding: "4px 12px 8px",
                  }}
                >
                  {section}
                </div>
              )}
              {items.map((item) => {
                const active = isActive(item.path);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: sidebarCollapsed ? "10px 0" : "8px 12px",
                      marginBottom: 2,
                      borderRadius: 6,
                      textDecoration: "none",
                      fontSize: 14,
                      fontWeight: active ? 500 : 400,
                      color: active ? "var(--c-primary)" : "var(--c-text-secondary)",
                      backgroundColor: active
                        ? "color-mix(in srgb, var(--c-primary) 8%, transparent)"
                        : "transparent",
                      justifyContent: sidebarCollapsed ? "center" : "flex-start",
                      transition: "background-color 0.15s ease, color 0.15s ease",
                    }}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <NavIcon name={item.icon} size={18} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        {!sidebarCollapsed && (
          <div
            style={{
              padding: "12px 20px",
              borderTop: "1px solid var(--c-border, rgba(0,0,0,0.1))",
              fontSize: 11,
              color: "var(--c-text-faint)",
            }}
          >
            Platform v1.0
          </div>
        )}
      </aside>

      {/* Main content */}
      <div
        style={{
          flex: 1,
          marginLeft: isMobile ? 0 : collapsed ? 60 : 240,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          transition: "margin-left 0.2s ease",
          backgroundColor: "var(--c-bg)",
        }}
      >
        {/* Critical alert banner */}
        <CriticalAlertBanner />

        {/* Top bar */}
        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 30,
            backgroundColor: "var(--c-surface)",
            borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))",
            padding: "10px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {isMobile && (
              <button
                onClick={() => setMobileOpen(true)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--c-text-secondary)",
                  padding: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Open menu"
              >
                <NavIcon name="collapse" size={22} />
              </button>
            )}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--c-text-faint)",
                }}
              >
                {activeNavItem.section}
              </div>
              <div
                style={{
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 550,
                  color: "var(--c-text)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: isMobile ? 180 : 320,
                }}
              >
                {activeNavItem.label}
              </div>
            </div>
            {!isMobile && (
              <div
                style={{
                  marginLeft: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid var(--c-border)",
                  backgroundColor: "var(--c-surface-2)",
                  fontSize: 11,
                  color: "var(--c-text-secondary)",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: serviceColor,
                  }}
                />
                {(fleetPulse?.activeDeployments ?? 0).toLocaleString()} live
                <span style={{ color: "var(--c-text-faint)" }}>·</span>
                {openAlerts} alerts
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <NotificationBell />
            <ThemeToggle theme={theme} onToggle={toggle} />
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: isMobile ? 16 : 24, overflowY: "auto" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
